import { useTranslate } from "#src/hooks";
import {
  PaletteResponse,
  PresenterProfileResponse,
  ContactResponse,
} from "pages/media/types";
import {
  AvatarShape,
  Avatar,
  TemplateType,
  TemplateState,
  ITEM_TYPES,
} from "../../../types";
import { ItemCardPreview } from "../../controls/ItemCardPreview";
import { ItemAlignmentFeature } from "../../features/ItemAlignmentFeature";
import { ApplySettingsFeature } from "../../features/ApplySettingsFeature";
import { PanelSection } from "../PanelSection";
import { AvatarShapeFeature } from "./features/AvatarShapeFeature";
import { AvatarShapeColorFeature } from "./features/AvatarShapeColorFeature";
import { AvatarStrokeFeature } from "./features/AvatarStrokeFeature";
import { Flex, Space, Switch, Typography } from "antd";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useCallback, useMemo, useState } from "react";
import {
  computeAvatarLayoutForRatio,
  getRelativeDimentionsToCalculateRadius,
  loadImageRatio,
  withFixedScriptIfNeeded,
} from "../../../helpers";
import { getAvatarImagePath } from "../../../helpers/itemsHelper";
import { RadiusIcon } from "components/icons/";
import { BorderRadius } from "../../controls/BorderRadius";
import { useChangeLayer } from "../../../hooks/useChangeLayer";
import { getPresenterImagePath } from "../../../hooks/usePresenterRatios";
import { ChapterAvatarModal } from "./ChapterAvatarModal";

export const AvatarTab = ({
  initialPresenter,
  brandKitPalette,
  previewWidth,
  previewHeight,
  presenterSelector,
}: {
  initialPresenter: PresenterProfileResponse | undefined;
  brandKitPalette: PaletteResponse | undefined;
  previewWidth: number;
  previewHeight: number;
  presenterSelector?: React.ReactNode;
}) => {
  const t = useTranslate();

  const [changeContactModalOpen, setChangeContactModalOpen] = useState(false);

  const {
    presenter,
    setPresenter,
    applyAvatarIdentityToAllChapters,
    syncPresenterToOtherTemplatesInChapter,
    applyPresenterStyleToTemplates,
    currentChapter,
    currentSlide,
    slideNameToTemplateIdMap,
    transition,
    updateTemplateState,
  } = useTemplateEditorStore((store) => ({
    presenter: store.templateState.presenter,
    setPresenter: store.setPresenter,
    applyAvatarIdentityToAllChapters: store.applyAvatarIdentityToAllChapters,
    syncPresenterToOtherTemplatesInChapter:
      store.syncPresenterToOtherTemplatesInChapter,
    applyPresenterStyleToTemplates: store.applyPresenterStyleToTemplates,
    currentChapter: store.currentChapter,
    currentSlide: store.currentSlide,
    slideNameToTemplateIdMap: store.slideNameToTemplateIdMap,
    transition: store.templateState.transition,
    updateTemplateState: store.updateTemplateState,
  }));

  const {
    projectId,
    storyboardSections,
    contacts,
    presenterRatiosById,
    templatesData: {
      projectMedia: instanceSettings,
      projectPresenters,
      isFlashProject,
    },
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      mediaAspectRatio,
      setChapterPresenter,
      setAllChaptersPresenter,
    },
  } = useTemplateData();

  const isTransitionScene =
    currentChapter.type === TemplateType.TRANSITION ||
    (currentSlide !== undefined &&
      mediaGenerationSettings?.chapters.some(
        (c) =>
          c.transition_template_id === slideNameToTemplateIdMap[currentSlide]
      ));

  // When a slide is selected (flash projects), currentChapter.sectionId is
  // empty even though the type is CHAPTER — resolve the slide's parent section
  // so per-chapter presenter changes target the right chapter.
  const effectiveSectionId =
    currentChapter.sectionId ||
    (currentSlide &&
      storyboardSections.find((section) =>
        section.slides.some((slide) => slide.uuid === currentSlide)
      )?.id);

  const { addTemplateUpdate, addBulkTemplateUpdate } = useTemplatesStatusStore(
    (state) => ({
      addTemplateUpdate: state.addTemplateUpdate,
      addBulkTemplateUpdate: state.addBulkTemplateUpdate,
    })
  );

  const { newHighestLayerIndex } = useChangeLayer(presenter);

  // The chapter's assigned avatar identity lives in the media settings
  // (per-chapter presenter_id) — the source of truth. Resolve it so the tab
  // reflects changes/apply-to-all even when the store template has no presenter
  // (e.g. avatar hidden). The store presenter still wins for the current
  // chapter's live edits; media settings covers everything else.
  const chapterPresenterId = mediaGenerationSettings?.chapters.find(
    (chapter) => chapter.chapter_id === effectiveSectionId
  )?.presenter_id;
  const chapterPresenter = chapterPresenterId
    ? projectPresenters?.find((p) => p.id === chapterPresenterId)
    : undefined;
  const resolvedPresenter =
    presenter.presenter ?? chapterPresenter ?? initialPresenter;

  // Preview the full avatar image (extracted/custom) rather than the cropped
  // thumbnail so the preview reflects the avatar's real aspect ratio, matching
  // the canvas. Fall back to the thumbnail when no full image exists.
  const previewAssetPath =
    getPresenterImagePath(resolvedPresenter) ??
    resolvedPresenter?.face_profile?.thumbnail_asset_path ??
    undefined;
  const previewAssetId =
    resolvedPresenter?.face_profile?.extracted_asset_id ??
    resolvedPresenter?.face_profile?.custom_asset_id ??
    resolvedPresenter?.face_profile?.thumbnail_asset_id ??
    undefined;

  const isChapter = currentChapter.type === TemplateType.CHAPTER;

  // Chapters and transitions can both carry an avatar (unlike intro/closing
  // scenes), so the toggle and appearance panel extend to transitions too —
  // but chapter-only affordances (apply-to-all, helper text) stay gated on
  // `isChapter` alone further down.
  const canToggleAvatar = isChapter || isTransitionScene;

  const isVoiceOnlyPresenter = !resolvedPresenter?.face_profile;

  const computeAvatarSizing = useCallback(
    async (presenterToUse: PresenterProfileResponse) => {
      const avatarImageUrl = getAvatarImagePath({
        ...presenter,
        presenter: presenterToUse,
      });
      if (!avatarImageUrl || !mediaAspectRatio) return undefined;
      try {
        const ratio = await loadImageRatio(avatarImageUrl);
        return computeAvatarLayoutForRatio(
          presenter.size,
          presenter.position,
          ratio,
          mediaAspectRatio
        );
      } catch (error) {
        console.error("Error loading avatar image ratio", error);
        return undefined;
      }
    },
    [presenter, mediaAspectRatio]
  );

  const onAvatarVisibilityChange = (value: boolean) => {
    if (!value) {
      setPresenter({
        presenter: undefined,
        layerIndex: newHighestLayerIndex,
      });
      addTemplateUpdate();
      return;
    }
    setPresenter({
      presenter: resolvedPresenter,
      layerIndex: newHighestLayerIndex,
    });
    addTemplateUpdate();

    const newTransition = withFixedScriptIfNeeded(
      transition,
      !!isTransitionScene
    );
    if (newTransition !== transition) {
      updateTemplateState({ transition: newTransition });
    }

    if (!resolvedPresenter) return;
    void computeAvatarSizing(resolvedPresenter).then((sizing) => {
      if (!sizing) return;
      setPresenter(sizing);
      addTemplateUpdate();
    });
  };

  // Presenter identity is a per-chapter setting (mediaGenerationSettings
  // .chapters[].presenter_id), so every scene belonging to this chapter —
  // every slide plus its transition (flash), or the chapter/transition pair
  // (non-flash) — must be kept in sync when the identity changes on any one
  // of them.
  const getOtherTemplateIdsInChapter = useCallback(
    (sectionId: string): string[] => {
      if (isFlashProject) {
        return (
          storyboardSections
            .find((section) => section.id === sectionId)
            ?.slides.map((slide) => slide.uuid) ?? []
        );
      }
      const chapterSettings = mediaGenerationSettings?.chapters.find(
        (chapter) => chapter.chapter_id === sectionId
      );
      return [
        chapterSettings?.chapter_template_id,
        chapterSettings?.transition_template_id,
      ].filter((id): id is string => !!id);
    },
    [isFlashProject, storyboardSections, mediaGenerationSettings]
  );

  // Chapters whose assigned presenter is voice-only never render an avatar —
  // nothing for style to affect there, so exclude their template ids upfront.
  const voiceOnlyTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    mediaGenerationSettings?.chapters.forEach((chapter) => {
      // No presenter_id means the chapter uses the project default, like resolvedPresenter.
      const assignedPresenter =
        (chapter.presenter_id
          ? projectPresenters?.find((p) => p.id === chapter.presenter_id)
          : undefined) ?? initialPresenter;
      if (assignedPresenter?.face_profile) return;

      if (isFlashProject) {
        storyboardSections
          .find((section) => section.id === chapter.chapter_id)
          ?.slides.forEach((slide) => ids.add(slide.uuid));
      } else {
        if (chapter.chapter_template_id) ids.add(chapter.chapter_template_id);
        if (chapter.transition_template_id)
          ids.add(chapter.transition_template_id);
      }
    });
    return ids;
  }, [
    mediaGenerationSettings,
    projectPresenters,
    initialPresenter,
    isFlashProject,
    storyboardSections,
  ]);

  // Needed to show the avatar on scenes where it's hidden: each chapter's own identity, and which scenes are transitions.
  const avatarApplyTargets = useMemo(() => {
    const identityByTemplateId = new Map<string, PresenterProfileResponse>();
    const transitionTemplateIds = new Set<string>();
    // Loop over sections, not settings rows: a chapter may have no settings row yet.
    storyboardSections.forEach((section) => {
      const chapter = mediaGenerationSettings?.chapters.find(
        (c) => c.chapter_id === section.id
      );
      const assignedPresenter =
        (chapter?.presenter_id
          ? projectPresenters?.find((p) => p.id === chapter.presenter_id)
          : undefined) ?? initialPresenter;
      getOtherTemplateIdsInChapter(section.id).forEach((id) => {
        if (assignedPresenter) identityByTemplateId.set(id, assignedPresenter);
        const templateId = isFlashProject ? slideNameToTemplateIdMap[id] : id;
        if (templateId && templateId === chapter?.transition_template_id) {
          transitionTemplateIds.add(id);
        }
      });
    });
    return { identityByTemplateId, transitionTemplateIds };
  }, [
    storyboardSections,
    mediaGenerationSettings,
    projectPresenters,
    initialPresenter,
    getOtherTemplateIdsInChapter,
    isFlashProject,
    slideNameToTemplateIdMap,
  ]);

  const applyAvatarStyleToTemplates = useCallback(
    (templateIds: string[], partial: Partial<TemplateState>) => {
      if (!partial.presenter) return;
      applyPresenterStyleToTemplates(
        templateIds.filter((id) => !voiceOnlyTemplateIds.has(id)),
        partial.presenter,
        { ...avatarApplyTargets, presenterRatiosById, mediaAspectRatio }
      );
    },
    [
      voiceOnlyTemplateIds,
      applyPresenterStyleToTemplates,
      avatarApplyTargets,
      presenterRatiosById,
      mediaAspectRatio,
    ]
  );

  const handleChapterAvatarSelected = useCallback(
    async (contact: ContactResponse) => {
      if (!effectiveSectionId) return;

      const newPresenter = contact.presenter as PresenterProfileResponse;
      const otherTemplateIds = getOtherTemplateIdsInChapter(effectiveSectionId);

      if (!newPresenter.face_profile) {
        // Voice-only contact: no avatar to render, so hide it for this chapter
        // while still persisting the presenter_id (used for the voice).
        const partial = { presenter: undefined, contactId: contact.id };
        setPresenter(partial);
        addTemplateUpdate();
        syncPresenterToOtherTemplatesInChapter(
          partial,
          otherTemplateIds,
          mediaAspectRatio
        );
      } else {
        // Bring the avatar to the front — otherwise it can inherit a stale
        // layerIndex from before it was hidden and end up buried behind
        // content added since.
        const partial = {
          presenter: newPresenter,
          contactId: contact.id,
          layerIndex: newHighestLayerIndex,
        };
        setPresenter(partial);
        addTemplateUpdate();
        syncPresenterToOtherTemplatesInChapter(
          partial,
          otherTemplateIds,
          mediaAspectRatio
        );

        // Recompute size/position against the new avatar's image ratio so a
        // wider/narrower avatar keeps a sensible layout.
        const sizing = await computeAvatarSizing(newPresenter);
        if (sizing) {
          setPresenter(sizing);
          addTemplateUpdate();
        }
      }

      await setChapterPresenter(effectiveSectionId, contact.presenter_id);
    },
    [
      setPresenter,
      addTemplateUpdate,
      computeAvatarSizing,
      setChapterPresenter,
      effectiveSectionId,
      getOtherTemplateIdsInChapter,
      syncPresenterToOtherTemplatesInChapter,
      mediaAspectRatio,
      newHighestLayerIndex,
    ]
  );

  const handleApplyAvatarToAllChapters = useCallback(
    async (contact?: ContactResponse) => {
      const presenterProfileId =
        contact?.presenter_id ?? presenter.presenter?.id;
      if (!presenterProfileId) return;

      const newPresenter =
        //PresenterProfileResponse type is patched with voice category type in types.ts,
        // ContactResponse's PresenterProfileResponse does not have strict category type.
        (contact?.presenter as PresenterProfileResponse | undefined) ??
        presenter.presenter;
      const isVoiceOnly = !newPresenter?.face_profile;
      // Resize against the new avatar's own image ratio (like the
      // single-chapter apply does) instead of assuming the current chapter
      // was already resized for it — this button can be clicked without
      // applying to the current chapter first.
      const sizing = isVoiceOnly
        ? undefined
        : await computeAvatarSizing(newPresenter);

      // Persist the avatar identity per-chapter in the media settings
      // (presenter_id, upserting a row for every section), then propagate it to
      // every chapter's template in the store — passing the resized avatar's
      // size lets each chapter recompute its avatar layout for the new image
      // ratio. The recalculated layouts are persisted via the bulk template save.
      await setAllChaptersPresenter(
        presenterProfileId,
        storyboardSections.map((section) => section.id)
      );
      applyAvatarIdentityToAllChapters(
        {
          presenter: isVoiceOnly ? undefined : newPresenter,
          contactId: contact?.id ?? presenter.contactId,
          ...(sizing ?? { size: presenter.size }),
        },
        mediaAspectRatio
      );
      addBulkTemplateUpdate();
    },
    [
      presenter.size,
      presenter.presenter,
      presenter.contactId,
      computeAvatarSizing,
      setAllChaptersPresenter,
      storyboardSections,
      applyAvatarIdentityToAllChapters,
      mediaAspectRatio,
      addBulkTemplateUpdate,
    ]
  );

  const handleAvatarEdited = useCallback(
    async (
      contact: ContactResponse,
      updatedPresenter: PresenterProfileResponse | undefined
    ) => {
      const oldPresenterId = contact.presenter_id;
      const newPresenterId = updatedPresenter?.id;
      if (!newPresenterId || newPresenterId === oldPresenterId) return;

      // Chapters currently persisted with the old, now-orphaned presenter id.
      const affectedSectionIds = storyboardSections
        .map((section) => section.id)
        .filter(
          (sectionId) =>
            mediaGenerationSettings?.chapters.find(
              (c) => c.chapter_id === sectionId
            )?.presenter_id === oldPresenterId
        );
      if (affectedSectionIds.length === 0) return;

      const isVoiceOnly = !updatedPresenter.face_profile;
      const sizing = isVoiceOnly
        ? undefined
        : await computeAvatarSizing(updatedPresenter);
      const newPresenterPartial = {
        presenter: isVoiceOnly ? undefined : updatedPresenter,
        contactId: contact.id,
      };

      if (effectiveSectionId && affectedSectionIds.includes(effectiveSectionId)) {
        setPresenter({
          ...newPresenterPartial,
          layerIndex: newHighestLayerIndex,
          ...sizing,
        });
        addTemplateUpdate();
      }
      const otherTemplateIds = affectedSectionIds.flatMap(
        getOtherTemplateIdsInChapter
      );
      syncPresenterToOtherTemplatesInChapter(
        newPresenterPartial,
        otherTemplateIds,
        mediaAspectRatio
      );

      await setAllChaptersPresenter(newPresenterId, affectedSectionIds);
      addBulkTemplateUpdate();
    },
    [
      storyboardSections,
      mediaGenerationSettings,
      effectiveSectionId,
      computeAvatarSizing,
      setPresenter,
      addTemplateUpdate,
      getOtherTemplateIdsInChapter,
      syncPresenterToOtherTemplatesInChapter,
      mediaAspectRatio,
      setAllChaptersPresenter,
      addBulkTemplateUpdate,
      newHighestLayerIndex,
    ]
  );

  const relativeDimensionsToCalculateRadius =
    getRelativeDimentionsToCalculateRadius(previewWidth, previewHeight);

  // Voice-only presenters never get an avatar sprite on the slide, so the
  // store's presenter.contactId is undefined for them too (mapped from the
  // sprite, not the identity) — fall back through the chapter's presenter_id
  // like resolvedPresenter above, then to the project-wide default contact.
  const chapterContactId = chapterPresenterId
    ? contacts?.find((c) => c.presenter_id === chapterPresenterId)?.id
    : undefined;
  const currentContactId =
    presenter.contactId ?? chapterContactId ?? instanceSettings?.contact_id;

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.presenterTab.title")}
        subtitle={t("components.templates.settings.presenterTab.description")}
        primary
      />
      <PanelSection
        title={t("components.templates.settings.presenterTab.myPresenter")}
      >
        {presenterSelector ?? (
          <ItemCardPreview
            item={t(
              "components.templates.settings.presenterTab.title"
            ).toLowerCase()}
            cover={{
              path: previewAssetPath,
              assetId: previewAssetId,
            }}
            title={resolvedPresenter?.name}
            onClick={() => setChangeContactModalOpen(true)}
          />
        )}
        {canToggleAvatar && (
          <Typography.Text type="secondary">
            {t(
              "components.templates.settings.presenterTab.avatarAppliesToCurrentChapter"
            )}
          </Typography.Text>
        )}
      </PanelSection>
      <PanelSection>
        {canToggleAvatar && !isVoiceOnlyPresenter && (
          <Space align="center">
            <Switch
              size="small"
              checked={!!presenter.presenter}
              onChange={onAvatarVisibilityChange}
            />
            <Typography.Text>
              {t("components.templates.settings.showItem", {
                item: t(
                  "components.templates.settings.presenter"
                ).toLowerCase(),
              })}
            </Typography.Text>
          </Space>
        )}
      </PanelSection>
      {presenter.presenter &&
        canToggleAvatar &&
        !isVoiceOnlyPresenter &&
        brandKitPalette && (
          <>
            <PanelSection
              title={t("components.templates.settings.presenterTab.appearance")}
            >
              <AvatarShapeFeature
                brandKitPalette={brandKitPalette}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
              {presenter.avatarShape === AvatarShape.Box && (
                <Flex align="center" gap={8}>
                  <RadiusIcon />
                  <BorderRadius
                    value={presenter.stroke?.radius ?? 0}
                    maxValue={Math.round(
                      (presenter.size.width / 2) * previewWidth
                    )}
                    onChange={(value) => {
                      setPresenter({
                        stroke: { ...presenter.stroke, radius: value },
                      });
                      addTemplateUpdate();
                    }}
                    relativeDimentions={relativeDimensionsToCalculateRadius}
                    popover={false}
                    sliderProps={{
                      label: t(
                        "components.templates.settings.presenterTab.radius"
                      ),
                      style: {
                        marginLeft: 10,
                      },
                      containerStyle: undefined,
                    }}
                  />
                </Flex>
              )}
            </PanelSection>
            {presenter.avatarShape !== AvatarShape.Regular && (
              <PanelSection
                title={t("components.templates.settings.circleColor")}
              >
                <AvatarShapeColorFeature brandKitPalette={brandKitPalette} />
              </PanelSection>
            )}
            {presenter.avatarShape !== AvatarShape.Regular && (
              <PanelSection title={t("components.templates.settings.stroke")}>
                <AvatarStrokeFeature
                  brandKitPalette={brandKitPalette}
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                />
              </PanelSection>
            )}
            <PanelSection title={t("components.templates.settings.alignment")}>
              <ItemAlignmentFeature
                pickedItem={presenter}
                onChange={(value) => {
                  setPresenter(value as Partial<Avatar>);
                  addTemplateUpdate();
                }}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
            </PanelSection>
          </>
        )}
      {/* Outside the visible-avatar block so a hidden avatar can be applied too */}
      {canToggleAvatar && !isVoiceOnlyPresenter && (
        <PanelSection>
          <ApplySettingsFeature
            partialTemplateState={{ presenter }}
            itemType={ITEM_TYPES.Presenter}
            applyFn={applyAvatarStyleToTemplates}
            allSceneTypes
          />
        </PanelSection>
      )}
      {projectId && (
        <ChapterAvatarModal
          open={changeContactModalOpen}
          projectId={projectId}
          currentContactId={currentContactId}
          onContactSelected={handleChapterAvatarSelected}
          onApplyToAllChapters={handleApplyAvatarToAllChapters}
          onContactEdited={handleAvatarEdited}
          onClose={() => setChangeContactModalOpen(false)}
        />
      )}
    </>
  );
};
